import type { QueryLabel } from '../GlobalMapSearch.classify';
import type { SearchRuntimeFlags } from './runtimeContext';

export interface SearchExecutionPlan {
  runGisLayers: boolean;
  runGeocode: boolean;
  runStaticLayers: boolean;
  runDynamicLayers: boolean;
  runCalls: boolean;
  runTranscripts: boolean;
  runMessages: boolean;
  runAlerts: boolean;
}

/**
 * Pure mapping from the classifier's primary label + runtime data flags to
 * the set of executors the orchestrator should schedule.
 *
 * Behavior matches the inline gates that used to live in `useExecuteSearch`:
 *   - Dynamic (Axon + generic dynamic spatial layers), static layers, and
 *     geocode/POI run only when the query reads as a `name` or `location`.
 *     `runDynamicLayers` is always true on name/location so the Axon
 *     pipeline still fires even if no other dynamic layers are loaded; the
 *     generic dynamic layer search internally short-circuits on empty
 *     `layers`.
 *   - GIS layer search runs whenever there are active layers; per-kind
 *     filters (skip hydrant on phone, restrict kinds on parcel) stay inside
 *     the GIS executor itself.
 */
export function getSearchExecutionPlan(
  primary: QueryLabel | undefined,
  flags: SearchRuntimeFlags,
): SearchExecutionPlan {
  switch (primary) {
    case 'name':
    case 'location':
      return {
        runGisLayers: flags.hasGisLayers,
        runGeocode: flags.hasMapCenter,
        runStaticLayers: flags.hasActiveStaticLayers,
        runDynamicLayers: true,
        runCalls: flags.hasCalls,
        // Transcripts can hold names ("John"), locations ("Main St"), and
        // free-form context — search them on any name/location query.
        runTranscripts: flags.hasTranscriptions,
        // Same reasoning as transcripts: chat messages tied to active
        // calls often contain caller names, addresses, and context.
        runMessages: flags.hasMessages,
        runAlerts: flags.hasAlerts,
      };
    case 'phone':
      return {
        runGisLayers: flags.hasGisLayers,
        runGeocode: false,
        runStaticLayers: false,
        runDynamicLayers: false,
        runCalls: flags.hasCalls,
        runTranscripts: false,
        runMessages: false,
        // Alert ids are often digit-heavy, so phone-shaped queries can
        // still hit the alert matcher.
        runAlerts: flags.hasAlerts,
      };
    case 'parcel':
    case 'place':
    case 'badge':
    default:
      return {
        runGisLayers: flags.hasGisLayers,
        runGeocode: false,
        runStaticLayers: false,
        runDynamicLayers: false,
        runCalls: false,
        // Free-text fallback — still useful to scan transcripts for the
        // exact substring even when the classifier didn't bucket it.
        runTranscripts: flags.hasTranscriptions,
        runMessages: flags.hasMessages,
        runAlerts: flags.hasAlerts,
      };
  }
}
